<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { firstDayOfMonth, formatAmount, formatMoney, toIsoDate } from '../utils/format'
import type {
  CashFlowSummaryRow,
  CashFlowSummaryTotals,
  CurrencyOption,
  ReportGroupBy,
} from '../types/finance'

const { t } = useI18n()
const { cashFlowSummary, listCurrencies } = useFinanceApi()

const today = toIsoDate()
const currencies = ref<CurrencyOption[]>([])
const rows = ref<CashFlowSummaryRow[]>([])
const totals = ref<CashFlowSummaryTotals | null>(null)
const loading = ref(true)
const error = ref('')

const filters = ref({
  date_from: firstDayOfMonth(),
  date_to: today,
  currency_code: '',
  group_by: 'day' as ReportGroupBy,
})

const groupOptions: ReportGroupBy[] = ['day', 'week', 'month']

const maxMagnitude = computed(() => {
  const values = rows.value.flatMap((row) => [Math.abs(Number(row.cash_in ?? 0)), Math.abs(Number(row.cash_out ?? 0))])
  return values.length ? Math.max(...values) : 0
})

function barWidth(value: number): string {
  if (!maxMagnitude.value) return '0%'
  return `${Math.min(100, (Math.abs(value) / maxMagnitude.value) * 100)}%`
}

const validationError = computed(() =>
  filters.value.date_from > filters.value.date_to ? t('finance.common.invalidRange') : '',
)

async function load() {
  loading.value = true
  error.value = ''
  if (validationError.value) {
    error.value = validationError.value
    loading.value = false
    return
  }
  try {
    const result = await cashFlowSummary({
      date_from: filters.value.date_from,
      date_to: filters.value.date_to,
      currency_code: filters.value.currency_code,
      group_by: filters.value.group_by,
    })
    rows.value = result.rows
    totals.value = result.summary
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadCurrencies() {
  try {
    currencies.value = await listCurrencies()
  } catch {
    currencies.value = []
  }
}

onMounted(() => {
  void loadCurrencies()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.nav.cashFlow') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="filters.date_from" class="date-input" type="date">
    <input v-model="filters.date_to" class="date-input" type="date">
    <select v-model="filters.currency_code" class="filter-select">
      <option value="">{{ t('finance.common.allCurrencies') }}</option>
      <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
        {{ currency.code }}
      </option>
    </select>
    <select v-model="filters.group_by" class="filter-select">
      <option v-for="option in groupOptions" :key="option" :value="option">
        {{ t(`finance.groupBy.${option}`) }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.apply') }}
    </button>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.reports.cashIn') }}</span>
        <span class="summary-value">{{ formatAmount(totals?.cash_in_total ?? 0) }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.reports.cashOut') }}</span>
        <span class="summary-value">{{ formatAmount(totals?.cash_out_total ?? 0) }}</span>
      </div>
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.reports.netTotal') }}</span>
        <span class="summary-value">{{ formatAmount(totals?.net_cash_flow ?? 0) }}</span>
      </div>
    </div>

    <div v-if="rows.length === 0" class="state-message"><p>{{ t('finance.reports.empty') }}</p></div>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.reports.period') }}</th>
            <th class="cell-num">{{ t('finance.reports.cashIn') }}</th>
            <th class="cell-num">{{ t('finance.reports.cashOut') }}</th>
            <th class="cell-num">{{ t('finance.reports.net') }}</th>
            <th>{{ t('finance.common.detail') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.period">
            <td class="cell-name">{{ row.period }}</td>
            <td class="cell-num is-positive">{{ formatAmount(row.cash_in) }}</td>
            <td class="cell-num is-negative">{{ formatAmount(row.cash_out) }}</td>
            <td class="cell-num" :class="Number(row.net) < 0 ? 'is-negative' : 'is-positive'">
              {{ formatAmount(row.net) }}
            </td>
            <td>
              <div class="bar-track" :title="formatMoney(row.cash_in, filters.currency_code || undefined)">
                <div class="bar-fill" :style="{ width: barWidth(row.cash_in) }" />
              </div>
              <div class="bar-track" style="margin-top: 4px">
                <div
                  class="bar-fill is-negative"
                  :style="{ width: barWidth(row.cash_out) }"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
</template>
